import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';

// Supported languages configuration
export const languages = {
  en: { 
    name: 'English', 
    nativeName: 'English',
    flag: '🇺🇸',
    dir: 'ltr'
  },
  'en-GB': { 
    name: 'English (UK)', 
    nativeName: 'English (UK)',
    flag: '🇬🇧',
    dir: 'ltr'
  },
  fr: { 
    name: 'French', 
    nativeName: 'Français',
    flag: '🇫🇷',
    dir: 'ltr'
  },
  ja: { 
    name: 'Japanese', 
    nativeName: '日本語',
    flag: '🇯🇵',
    dir: 'ltr'
  },
  'zh-CN': { 
    name: 'Simplified Chinese', 
    nativeName: '简体中文',
    flag: '🇨🇳',
    dir: 'ltr'
  },
  'zh-TW': { 
    name: 'Traditional Chinese', 
    nativeName: '繁體中文',
    flag: '🇹🇼',
    dir: 'ltr'
  },
  ko: { 
    name: 'Korean', 
    nativeName: '한국어',
    flag: '🇰🇷',
    dir: 'ltr'
  },
  es: { 
    name: 'Spanish', 
    nativeName: 'Español',
    flag: '🇪🇸',
    dir: 'ltr'
  },
  pt: { 
    name: 'Portuguese', 
    nativeName: 'Português',
    flag: '🇵🇹',
    dir: 'ltr'
  },
  de: { 
    name: 'German', 
    nativeName: 'Deutsch',
    flag: '🇩🇪',
    dir: 'ltr'
  },
  nl: { 
    name: 'Dutch', 
    nativeName: 'Nederlands',
    flag: '🇳🇱',
    dir: 'ltr'
  },
  sv: { 
    name: 'Swedish', 
    nativeName: 'Svenska',
    flag: '🇸🇪',
    dir: 'ltr'
  },
  no: { 
    name: 'Norwegian', 
    nativeName: 'Norsk',
    flag: '🇳🇴',
    dir: 'ltr'
  },
  da: { 
    name: 'Danish', 
    nativeName: 'Dansk',
    flag: '🇩🇰',
    dir: 'ltr'
  },
  it: { 
    name: 'Italian', 
    nativeName: 'Italiano',
    flag: '🇮🇹',
    dir: 'ltr'
  },
  ar: { 
    name: 'Arabic', 
    nativeName: 'العربية',
    flag: '🇸🇦',
    dir: 'rtl'  // Right-to-left
  },
  sw: { 
    name: 'Swahili', 
    nativeName: 'Kiswahili',
    flag: '🇰🇪',
    dir: 'ltr'
  },
  ru: { 
    name: 'Russian', 
    nativeName: 'Русский',
    flag: '🇷🇺',
    dir: 'ltr'
  }
};

// Language detection configuration
const detectionOptions = {
  // Order of detection methods
  order: [
    'localStorage',      // Check if user has previously selected
    'navigator',         // Browser language
    'htmlTag',          // HTML lang attribute
    'path',             // URL path
    'subdomain'         // Subdomain
  ],
  
  // Keys for localStorage
  lookupLocalStorage: 'miela_language',
  
  // Cache user language preference
  caches: ['localStorage'],
  
  // Don't use cookies for privacy
  excludeCacheFor: ['cimode'],
  
  // Conversion rules for language codes
  convertDetectedLanguage: (lng) => {
    // Normalize language codes
    if (lng === 'zh-Hans') return 'zh-CN';
    if (lng === 'zh-Hant') return 'zh-TW';
    return lng;
  }
};

i18n
  // Load translation files
  .use(Backend)
  
  // Detect user language
  .use(LanguageDetector)
  
  // Pass i18n instance to react-i18next
  .use(initReactI18next)
  
  // Initialize i18next
  .init({
    // Fallback language if translation is missing
    fallbackLng: 'en',
    
    // Default language (English)
    lng: 'en',
    
    // Supported languages
    supportedLngs: Object.keys(languages),
    
    // Namespace for translations
    ns: ['common', 'products', 'navigation', 'forms', 'about'],
    defaultNS: 'common',
    
    // Language detection
    detection: detectionOptions,
    
    // React suspense mode
    react: {
      useSuspense: false
    },
    
    // Backend configuration for loading translation files
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json'
    },
    
    // Interpolation settings
    interpolation: {
      escapeValue: false, // React already escapes
      formatSeparator: ','
    },
    
    // Missing key handler for development
    saveMissing: process.env.NODE_ENV === 'development',
    missingKeyHandler: (lng, ns, key) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`Missing translation: [${lng}][${ns}] ${key}`);
      }
    },
    
    // Load all language codes including regions (zh-CN, en-GB, etc.)
    load: 'all',
    
    // Don't clean code (keep zh-CN as zh-CN)
    cleanCode: false
  });

// Direction helper
export const getDirection = (lng) => {
  return languages[lng]?.dir || 'ltr';
};

// Currency helper based on language
export const getCurrency = (lng) => {
  const currencyMap = {
    'en': 'USD',
    'en-GB': 'GBP',
    'fr': 'EUR',
    'ja': 'JPY',
    'zh-CN': 'CNY',
    'zh-TW': 'TWD',
    'ko': 'KRW',
    'es': 'EUR',
    'pt': 'EUR',
    'de': 'EUR',
    'nl': 'EUR',
    'sv': 'SEK',
    'no': 'NOK',
    'da': 'DKK',
    'it': 'EUR',
    'ar': 'SAR',
    'sw': 'KES',
    'ru': 'RUB'
  };
  return currencyMap[lng] || 'JPY'; // Default to JPY for MIELA MAISON
};

export default i18n;
